import api from "../../services/api.js";

/**
 * Calcula % de consumo: (horasReais / horasPrevistas) * 100
 * Retorna null quando não há dados.
 */
export function calcCnsum(previstas, reais) {
  if (!previstas || previstas === 0) return null;
  return Math.round((reais / previstas) * 100);
}

/**
 * Cor do % de consumo: verde ≤100%, laranja ≤130%, vermelho >130%
 */
export function cnsumColor(val) {
  if (val === null) return "#6B7280";
  if (val <= 100) return "#09d955";
  if (val <= 130) return "#f29007";
  return "#fa0c0c";
}

export function computeEpicSpanFromStories(stories, fallbackStart, fallbackEnd) {
  const spans = (stories || [])
    .filter((s) => s.executionStartDate)
    .map(s => {
      const start = new Date(s.executionStartDate);
      const hours = Number(s.estimated_hours) || 0;
      const durationDays = Math.max(1, Math.ceil(hours / HOURS_PER_DAY));
      const end = addDays(start, durationDays);

      return { start, end };
    });

  if (spans.length === 0) {
    return { start: fallbackStart, end: fallbackEnd };
  }

  const start = new Date(Math.min(...spans.map((s) => s.start.getTime())));
  const end = new Date(Math.max(...spans.map((s) => s.end.getTime())));

  return { start, end };
}

export function buildGanttTasks(rawTasks, dependenciesData, epicStoriesMap = {}) {
  const result = [];

  rawTasks.forEach((task) => {
    const prev = Number(task.horas_previstas) || 0;
    const real = Number(task.horas_reais) || 0;
    const progress = Number(task.progress) || 0;
    const baseId = String(task.id_epico);

    const stories = epicStoriesMap[baseId] || [];
    const { start, end } = computeEpicSpanFromStories(
      stories,
      new Date(task.start_date),
      new Date(task.end_date)
    );

    const totalDays = Math.max(1, daysBetween(start, end));
    const cnsum = calcCnsum(prev, real);

    // const row = dependenciesData.find(
    //   (d) => String(d.id_pai) === `epic-${baseId}`
    // );

    const deps = dependenciesData
      .filter((d) => (d.id_filhos || []).map(String).includes(`epic-${baseId}`))
      .map((d) => String(d.id_pai));
    const normalizedDeps = deps
      .map((id) => {
        if (!id.includes("-")) return id;
        const [type, ...rest] = id.split("-");
        if (type === "epic") return rest[0];
        if (type === "story") return `story-${rest.join("-")}`;
        return rest[rest.length - 1];
      })
      .filter(Boolean);

    // 1) Linha principal — Planejamento (agora reflete o span das histórias)
    result.push({
      id: baseId,
      name: task.title || task.name || "",
      start,
      end,
      type: task.type || "task",
      progress: 0,
      dependencies: normalizedDeps,
      project: task.project,
      title: task.title,
      status: task.status,
      horasPrevistas: prev,
      horasReais: real,
      cnsum,
      rowKind: "main",
      styles: {
        backgroundColor: "#7DD3FC",
        backgroundSelectedColor: "#38BDF8",
        progressColor: "transparent",
        progressSelectedColor: "transparent",
      },
    });

    // 2) Consumo (laranja)
    if (cnsum !== null) {
      const consumoDays = Math.max(1, Math.round(totalDays * (real / (prev || 1))));
      result.push({
        id: `${baseId}__consumo`,
        name: "",
        start,
        end: addDays(start, consumoDays),
        type: "task",
        progress: 0,
        dependencies: [],
        isDisabled: true,
        rowKind: "consumo",
        cnsum,
        styles: {
          backgroundColor: "#F97316",
          backgroundSelectedColor: "#f29007",
          progressColor: "transparent",
          progressSelectedColor: "transparent",
        },
      });
    }

    // 3) Progresso (azul escuro)
    if (progress > 0) {
      const progressoDays = Math.max(1, Math.round(totalDays * (progress / 100)));
      result.push({
        id: `${baseId}__progresso`,
        name: "",
        start,
        end: addDays(start, progressoDays),
        type: "task",
        progress: 0,
        dependencies: [],
        isDisabled: true,
        rowKind: "progresso",
        progressPct: progress,
        styles: {
          backgroundColor: "#1D4ED8",
          backgroundSelectedColor: "#1E40AF",
          progressColor: "transparent",
          progressSelectedColor: "transparent",
        },
      });
    }
  });

  return result;
}

export function buildStoryTask(
  story,
  epic,
  dependenciesData,
  storyIdToEpicId,
  getAssigneeName,
) {
  const prev = Number(story.estimated_hours) || 0;
  const real = Number(story.horasReais) || 0;
  const progress = Number(story.progress) || 0;

  const durationDays = Math.max(1, Math.ceil(prev/ HOURS_PER_DAY));

  const cnsum = calcCnsum(prev, real);

  // const row = dependenciesData.find(
  //   (d) => String(d.id_pai) === `story-${story.id}`
  // );

  const rawDeps = dependenciesData
    .filter((d) => (d.id_filhos || []).map(String).includes(`story-${story.id}`))
    .map((d) => String(d.id_pai));

  const normalizedDeps = rawDeps
    .map((depId) => {
      const [type, rawId] = depId.split("-");
      if (type === "story") {
        const parentEpicId = storyIdToEpicId[rawId];
        if (!parentEpicId) return null;
        return `story-${parentEpicId}-${rawId}`;
      }
      return null;
    })
    .filter(Boolean);

    // Início: usa parseLocal também, para evitar deslocamento de fuso
    const storyStart =
      parseLocalDate(story.plannedStartDate) || new Date(epic.start);

    // Fim: prioriza a data de fim prevista; senão, calcula pelas horas
    const plannedEnd = parseLocalDate(story.plannedEndDate);
    let storyEnd;

    if (plannedEnd && plannedEnd >= storyStart) {
      storyEnd = addDays(plannedEnd, 1); // inclui o dia final
    } else {
      storyEnd = addDays(storyStart, durationDays);
    }

    return {
      id: `story-${epic.id}-${story.id}`,
      name: getAssigneeName?.(story) || "Sem responsável",
      subject: story.subject,

      start: storyStart,
      end: storyEnd,

      type: "task",

      progress,
      cnsum,
      dependencies: normalizedDeps,

      isDisabled: false,
      rowKind: "storyMain",
      storyRef: story,

      styles: {
        backgroundColor: "#F97316",
        backgroundSelectedColor: "#f29007",
        progressColor: "#1D43D8",
        progressSelectedColor: "#1E40AF",
      },
    };
}

export function parseLocalDate(value) {
  if (!value) return null;
  const [year, month, day] = String(value).split("T")[0].split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

export function buildStoryExecutionTask(story, epic) {
  if (!story.executionStartDate) return null;

  const executionStart = parseLocalDate(story.executionStartDate);

  if (Number.isNaN(executionStart.getTime())) {
    console.warn(
      `Data de execução inválida para a história ${story.id}:`,
      story.executionStartDate,
    );
    return null;
  }

  // const planningStart = new Date(epic.start);
  // const planningEnd = new Date(epic.end);

  
  // const totalDays = Math.max(1, daysBetween(planningStart, planningEnd));

  // const progress = Math.min(
  //   100,
  //   Math.max(0, Number(story.progress) || 0),
  // );

  let executionEnd;
  const parsedEnd = parseLocalDate(story.executionEndDate);
  if (parsedEnd) {
    executionEnd = addDays(parsedEnd, 1);
  } else {
    // fallback: duração baseada nas horas estimadas da própria história
    const estimatedHours = Number(story.estimated_hours) || 0;
    const durationDays = Math.max(1, Math.ceil(estimatedHours / HOURS_PER_DAY));
    executionEnd = addDays(executionStart, durationDays);
  }

  // const executionDuration = Math.max(
  //   1,
  //   Math.round(totalDays * (progress / 100))
  // );

  const createdDate = story.created_date
    ? new Date(story.created_date)
    : null;

  const delayed =
    createdDate &&
    !Number.isNaN(createdDate.getTime()) &&
    executionStart > createdDate;

  return {
    id: `story-${epic.id}-${story.id}__execucao`,
    name: "",
    start: executionStart,
    end: executionEnd,
    type: "task",
    progress: 0,
    dependencies: [],
    isDisabled: true,
    rowKind: "execucao",
    styles: {
      backgroundColor: delayed ? "#fa0c0c" : "#09d955",
      backgroundSelectedColor: delayed ? "#B91C1C" : "#15803D",
      progressColor: "transparent",
      progressSelectedColor: "transparent",
    },
  };
}

export async function fetchStoriesWithAttributes(epicId, dataInicio) {
  const response = await api.get(`/epicUserStories/${epicId}`, {
    params: {
      date: dataInicio ? dataInicio.toISOString().split("T")[0] : undefined,
    },
  });
  const stories = response.data;

  return Promise.all(
    stories.map(async (story) => {
      try {
        const { data: attributes } = await api.get("/userStoryAttributeValues", {
          params: { userStoryId: story.id },
        });

        const progressAttribute = attributes.find(
          (attr) => attr.nome_atributo === "Progresso (%)"
        );
        const expectedTimeAttribute = attributes.find(
          (attr) => attr.nome_atributo === "Tempo esperado"
        );
        const workTimeAttribute = attributes.find(
          (attr) => attr.nome_atributo === "Horas Trabalhadas (Diariamente)"
        );
        const startDateAttribute = attributes.find(
          (attr) => attr.nome_atributo === "Data de Início"
        );

        const endDateAttribute = attributes.find(
          (attr) => attr.nome_atributo.trim().toLowerCase() === "data de fim"
        );

        const plannedStartAttribute = attributes.find(
          (attr) => attr.nome_atributo === "Data de Início Prevista"
        );

        const plannedEndAttribute = attributes.find(
          (attr) => attr.nome_atributo.trim().toLowerCase() === "data de fim prevista"
        );

        const rawProgress = progressAttribute?.valor_atributo;
        const normalizedProgress = rawProgress
          ? String(rawProgress).replace(",", ".").trim()
          : "";
        const parsedProgress = Number(normalizedProgress);

        const rawExpectedTime = expectedTimeAttribute?.valor_atributo;
        const normalizedExpectedTime = rawExpectedTime
          ? String(rawExpectedTime).replace("h", "").replace(",", ".").trim()
          : "";
        const parsedExpectedTime = Number(normalizedExpectedTime);

        const rawWorkTime = workTimeAttribute?.valor_atributo || "";
        const workTimeMatches = Array.from(
          String(rawWorkTime).matchAll(/-\s*([0-9]+(?:[.,][0-9]+)?)/g)
        );
        const parsedWorkHours = workTimeMatches
          .map((match) => Number(String(match[1]).replace(",", ".")))
          .filter((value) => Number.isFinite(value))
          .reduce((sum, value) => sum + value, 0);

        return {
          ...story,

          progress: Number.isFinite(parsedProgress) ? parsedProgress : story.progress || 0,

          estimated_hours: Number.isFinite(parsedExpectedTime)
            ? parsedExpectedTime
            : story.estimated_hours || 0,

          horasReais: parsedWorkHours > 0 ? parsedWorkHours : story.horasReais || 0,

          executionStartDate: startDateAttribute?.valor_atributo || null,
          
          executionEndDate: endDateAttribute?.valor_atributo || null,

          plannedStartDate: plannedStartAttribute?.valor_atributo || null,

          plannedStartAttrId: plannedStartAttribute?.id_atributo || null,

          plannedEndDate: plannedEndAttribute?.valor_atributo || null,
          
          plannedEndAttrId: plannedEndAttribute?.id_atributo || null,
        };
      } catch (error) {
        console.error(`Erro ao buscar atributos da história ${story.id}:`, error);
        return {
          ...story,
          progress: story.progress || 0,
          estimated_hours: story.estimated_hours || 0,
        };
      }
    })
  );
}

// Normaliza strings: remove acentos e converte para minúsculas
export function normalizeString(s) {
  if (!s) return "";
  try {
    return String(s)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  } catch (e) {
    return String(s)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }
}

export function daysBetween(a, b) {
  return Math.max(0, Math.round((b - a) / (1000 * 60 * 60 * 24)));
}

export function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function makePlaceholderRow(epicId, epicTask, rowKind) {
  return {
    id: `${epicId}_${rowKind}`,
    name: "",
    start: epicTask.start,
    end: epicTask.end,
    type: "task",
    progress: 0,
    isDisabled: true,
    rowKind,
    styles: {
      backgroundColor: "transparent",
      backgroundSelectedColor: "transparent",
      progressColor: "transparent",
      progressSelectedColor: "transparent",
    },
  };
}

export const HOURS_PER_DAY = 8;

export const COL_WIDTHS = {
  item: 260,
  horasPrev: 56,
  horasReal: 56,
  cnsum: 72,
  progresso: 80,
};

export const TOTAL_LIST_WIDTH =
  COL_WIDTHS.item +
  COL_WIDTHS.horasPrev +
  COL_WIDTHS.horasReal +
  COL_WIDTHS.cnsum +
  COL_WIDTHS.progresso;

export const headerCellStyle = (width, extra = {}) => ({
  width,
  minWidth: width,
  maxWidth: width,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  borderRight: "1px solid #E5E7EB",
  padding: "0 4px",
  fontSize: "11px",
  fontWeight: 600,
  color: "#374151",
  textAlign: "center",
  lineHeight: 1.2,
  ...extra,
});

export const cellStyle = (width, extra = {}) => ({
  width,
  minWidth: width,
  maxWidth: width,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRight: "1px solid #E5E7EB",
  padding: "0 4px",
  fontSize: "12px",
  color: "#111827",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  ...extra,
});